import {
  type ManuallyLaunchableWorkflow,
  type ProcessWorkflow,
  type Task,
  useClientProcessWorkflowQuery,
  useClientProcessWorkflowsQuery,
  useTasksQuery,
} from '@cartwheel/api/v1'
import {
  Button,
  EmptyState,
  LoadingView,
  Pagination,
  ProcessWorkflowTimeline,
  Tabs,
  useUrlEnumState,
} from '@cartwheel/components'
import * as DataTable from '@cartwheel/components/data-table'
import { TaskDrawer } from '@cartwheel/components/task-drawer'
import { formatDate, formatRelativeDays, titleize } from '@cartwheel/string-utils'
import { useState } from 'react'
import { StartNewWorkflowModal } from './start-new-workflow-modal'

type WorkflowStatus = ProcessWorkflow['status']

type SubTab = 'workflows' | 'tasks'

const SUB_TAB_VALUES = ['workflows', 'tasks'] as const

const TYPE_ICON: Record<string, string> = {
  joint_consent: 'fa-solid fa-people-arrows',
}
const DEFAULT_ICON = 'fa-solid fa-diagram-project'

const STATUS_LABEL: Record<WorkflowStatus, string> = {
  running: 'In Progress',
  completed: 'Complete',
  failed: 'Failed',
  canceled: 'Canceled',
}

const STATUS_CLASS: Record<WorkflowStatus, string> = {
  running: 'bg-sky-25 text-sea',
  completed: 'bg-charcoal-25 text-charcoal-50',
  failed: 'bg-orange-100 text-orange-800',
  canceled: 'bg-charcoal-25 text-charcoal-50',
}

const TASKS_PER_PAGE = 25

function EmptyStateIllustration({ label }: { label: string }) {
  return (
    <svg
      width="117"
      height="32"
      viewBox="0 0 117 32"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
      className="text-mint"
    >
      <rect x="2.87" y="2.82" width="25.11" height="25.41" transform="rotate(-15 15.42 15.52)" />
      <path
        d="M36.35 9C36.35 16.1 41.98 21.85 48.91 21.85C55.83 21.85 61.46 16.1 61.46 9H36.35Z"
        transform="rotate(30 48.91 15.43)"
      />
      <rect x="70.21" y="2.52" width="12.55" height="25.41" transform="rotate(15 76.49 15.22)" />
      <path d="M115.54 27.77H90.54L115.54 2.77V27.77Z" transform="rotate(-3.96 103.04 15.27)" />
    </svg>
  )
}

function WorkflowCardExpandedBody({
  clientId,
  workflowId,
}: {
  clientId: string
  workflowId: string
}) {
  const { data, isPending, error } = useClientProcessWorkflowQuery({
    path: { client_id: clientId, id: workflowId },
  })

  if (isPending) {
    return (
      <div className="border-t border-gray-stroke bg-sand-25 px-6 py-5 text-sm text-charcoal-50">
        Loading workflow detail…
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="border-t border-gray-stroke bg-sand-25 px-6 py-5 text-sm text-orange-700">
        Couldn't load workflow detail. Please try again.
      </div>
    )
  }

  if (data.unavailable) {
    return (
      <div className="border-t border-gray-stroke bg-sand-25 px-6 py-5 text-sm text-charcoal-50">
        Live workflow state unavailable.
      </div>
    )
  }

  return (
    <div className="border-t border-gray-stroke bg-sand-25 px-6 py-5">
      <ProcessWorkflowTimeline items={data.timeline} />
    </div>
  )
}

function WorkflowCard({ clientId, workflow }: { clientId: string; workflow: ProcessWorkflow }) {
  const [expanded, setExpanded] = useState(false)
  const icon = TYPE_ICON[workflow.type] ?? DEFAULT_ICON
  const statusClass = STATUS_CLASS[workflow.status]
  const statusLabel = STATUS_LABEL[workflow.status]

  return (
    <div className="overflow-hidden rounded-lg border border-gray-stroke bg-white">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-sand-25"
      >
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-sky-25">
          <i className={`${icon} text-sea`} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold text-charcoal">{workflow.display_name}</div>
          <div className="text-sm text-charcoal-50">
            Started {formatDate(workflow.started_at)} • Last Activity{' '}
            {formatDate(workflow.updated_at)}
          </div>
        </div>
        <span className={`inline-block rounded px-2 py-0.5 text-sm font-medium ${statusClass}`}>
          {statusLabel}
        </span>
        <i
          className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-charcoal-50`}
          aria-hidden
        />
      </button>
      {expanded && <WorkflowCardExpandedBody clientId={clientId} workflowId={workflow.id} />}
    </div>
  )
}

interface PanelProps {
  clientId: string
}

interface WorkflowsPanelProps extends PanelProps {
  hasManuallyLaunchableWorkflows: boolean
  onStartNewWorkflow: () => void
}

function WorkflowsPanel({
  clientId,
  hasManuallyLaunchableWorkflows,
  onStartNewWorkflow,
}: WorkflowsPanelProps) {
  const { data, isPending, error } = useClientProcessWorkflowsQuery({
    path: { client_id: clientId },
    query: { status: 'running' },
  })

  if (isPending) return <LoadingView title="Loading workflows..." />
  if (error) {
    return (
      <div className="text-red-600">
        <h3 className="text-lg font-semibold">Error loading workflows</h3>
        <p className="mt-1">Please refresh the page or try again later.</p>
      </div>
    )
  }

  const workflows: ProcessWorkflow[] = data?.workflows ?? []

  if (workflows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4">
        <EmptyState
          title="No workflows running yet"
          description="Starting a workflow kicks off a predetermined series of automated and manual tasks"
          illustration={<EmptyStateIllustration label="No workflows running" />}
        />
        {hasManuallyLaunchableWorkflows && (
          <Button variant="primary" onClick={onStartNewWorkflow}>
            Start New Workflow
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {workflows.map((workflow) => (
        <WorkflowCard key={workflow.id} clientId={clientId} workflow={workflow} />
      ))}
    </div>
  )
}

function AssigneeCell({ task }: { task: Task }) {
  if (!task.assignee) {
    return <span className="text-charcoal-50">Unassigned</span>
  }
  return <span>{task.assignee.name}</span>
}

type TasksSortColumn = 'type' | 'created_at' | 'assignee'
type SortDirection = 'asc' | 'desc'

function TasksPanel({ clientId }: PanelProps) {
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<TasksSortColumn>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)

  const handleSort = (column: TasksSortColumn) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
    setPage(1)
  }

  const { data, isPending, error } = useTasksQuery({
    query: {
      resource_id: clientId,
      'statuses[]': ['open'],
      sort_by: sortBy,
      sort_direction: sortDirection,
      per_page: TASKS_PER_PAGE,
      page,
    },
  })

  if (isPending) return <LoadingView title="Loading tasks..." />
  if (error) {
    return (
      <div className="text-red-600">
        <h3 className="text-lg font-semibold">Error loading tasks</h3>
        <p className="mt-1">Please refresh the page or try again later.</p>
      </div>
    )
  }

  const tasks: Task[] = data?.tasks ?? []
  const totalCount = data?.total_count ?? 0
  const totalPages = data?.total_pages ?? 1
  const from = totalCount === 0 ? 0 : (page - 1) * TASKS_PER_PAGE + 1
  const to = Math.min(page * TASKS_PER_PAGE, totalCount)

  if (totalCount === 0) {
    return (
      <EmptyState
        title="No open tasks"
        description="Tasks for this client will appear here."
        illustration={<EmptyStateIllustration label="No open tasks" />}
      />
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-stroke bg-white">
      <DataTable.Root>
        <DataTable.Header>
          <DataTable.Row>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortBy === 'type' ? sortDirection : null}
              onSort={() => handleSort('type')}
            >
              Task
            </DataTable.HeaderCell>
            <DataTable.HeaderCell>Workflow</DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortBy === 'created_at' ? sortDirection : null}
              onSort={() => handleSort('created_at')}
            >
              Created
            </DataTable.HeaderCell>
            <DataTable.HeaderCell>Days open</DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortBy === 'assignee' ? sortDirection : null}
              onSort={() => handleSort('assignee')}
            >
              Assignee
            </DataTable.HeaderCell>
          </DataTable.Row>
        </DataTable.Header>
        <DataTable.Body>
          {tasks.map((task) => (
            <DataTable.Row
              key={task.id}
              role="button"
              tabIndex={0}
              className="h-12 cursor-pointer bg-white even:!bg-white hover:bg-sand-25 focus:outline-none focus:ring-2 focus:ring-forest focus:ring-inset"
              onClick={() => setOpenTaskId(task.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setOpenTaskId(task.id)
                }
              }}
            >
              <DataTable.Cell label="Task">{task.title}</DataTable.Cell>
              <DataTable.Cell label="Workflow">
                {task.workflow?.type ? (
                  titleize(task.workflow.type)
                ) : (
                  <span className="text-charcoal-50">—</span>
                )}
              </DataTable.Cell>
              <DataTable.Cell label="Created">{formatDate(task.created_at)}</DataTable.Cell>
              <DataTable.Cell label="Days open">
                {formatRelativeDays(task.created_at)}
              </DataTable.Cell>
              <DataTable.Cell label="Assignee">
                <AssigneeCell task={task} />
              </DataTable.Cell>
            </DataTable.Row>
          ))}
        </DataTable.Body>
      </DataTable.Root>

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />}

      <DataTable.Footer>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={totalCount}
          from={from}
          to={to}
          itemsPerPage={TASKS_PER_PAGE}
          hasNextPage={page < totalPages}
          hasPrevPage={page > 1}
          onPageChange={setPage}
          onItemsPerPageChange={() => {}}
          showItemsPerPageSelector={false}
        />
      </DataTable.Footer>
    </div>
  )
}

interface Props {
  clientId: string
}

export default function ClientWorkflowsList({ clientId }: Props) {
  const [activeTab, setActiveTab] = useUrlEnumState<SubTab>({
    paramName: 'workflows_tab',
    defaultValue: 'workflows',
    values: SUB_TAB_VALUES,
  })
  const [isModalOpen, setIsModalOpen] = useState(false)

  const workflowsCountQuery = useClientProcessWorkflowsQuery({
    path: { client_id: clientId },
    query: { status: 'running', per_page: 1, page: 1 },
  })

  const tasksCountQuery = useTasksQuery({
    query: {
      resource_id: clientId,
      'statuses[]': ['open'],
      per_page: 1,
      page: 1,
    },
  })

  const workflowsCount = workflowsCountQuery.data?.pagination?.total_count ?? 0
  const tasksCount = tasksCountQuery.data?.total_count ?? 0
  const manuallyLaunchableWorkflows: ManuallyLaunchableWorkflow[] =
    workflowsCountQuery.data?.manually_launchable_workflows ?? []
  const isShowingWorkflowsEmptyState = activeTab === 'workflows' && workflowsCount === 0

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold text-charcoal">Workflows</h2>
        {manuallyLaunchableWorkflows.length > 0 && !isShowingWorkflowsEmptyState && (
          <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
            New Workflow
          </Button>
        )}
      </div>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List ariaLabel="Workflows and tasks" className="mb-4">
          <Tabs.Tab value="workflows" count={workflowsCount}>
            Workflows
          </Tabs.Tab>
          <Tabs.Tab value="tasks" count={tasksCount}>
            Tasks
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="workflows">
          <WorkflowsPanel
            clientId={clientId}
            hasManuallyLaunchableWorkflows={manuallyLaunchableWorkflows.length > 0}
            onStartNewWorkflow={() => setIsModalOpen(true)}
          />
        </Tabs.Panel>
        <Tabs.Panel value="tasks">
          <TasksPanel clientId={clientId} />
        </Tabs.Panel>
      </Tabs>
      {isModalOpen && (
        <StartNewWorkflowModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          clientId={clientId}
          manuallyLaunchableWorkflows={manuallyLaunchableWorkflows}
          onLaunched={() => setIsModalOpen(false)}
        />
      )}
    </div>
  )
}
